import clsx from 'clsx';
import * as React from 'react';
import { HeadingContext, Provider } from 'react-aria-components';

import { DescriptionContext } from '../Description';
import { type LabelPropsInput, useLabelProps } from '../util/labeling';
import { CardDismissButton } from './CardDismissButton';

export type GenericCardHookProps = {
    className?: string;
    onDismiss?: () => void;
    kind?: 'default' | 'glass';
};

export type CardHookProps = GenericCardHookProps & LabelPropsInput;

export type CardResult = {
    className: string;
    'aria-labelledby'?: string | undefined;
    'aria-describedby'?: string | undefined;
    'aria-label'?: string | undefined;
};

export type CardProps = React.ComponentPropsWithoutRef<'div'> & GenericCardHookProps;

/**
 * Card component to wrap content in a card.
 */
export const Card = React.forwardRef(function Card(
    props: CardProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const headingId = null;
    const descriptionId = null;
    const { onDismiss, children, ...rest } = props;
    const cardProps = useCard({ ...props, headingId, descriptionId });

    return (
        <CardProvider headingId={headingId} descriptionId={descriptionId}>
            <div ref={ref} {...rest} {...cardProps}>
                {onDismiss ? <CardDismissButton onDismiss={onDismiss} /> : null}
                {children}
            </div>
        </CardProvider>
    );
});

export function CardProvider(props: {
    headingId: string | null;
    descriptionId: string | null;
    children: React.ReactNode;
}) {
    return (
        <Provider
            values={[
                [
                    HeadingContext,
                    {
                        level: 2,
                        className: 'text-ui-heading-small',
                        id: props.headingId ?? undefined,
                    },
                ],
                [
                    DescriptionContext,
                    {
                        id: props.descriptionId ?? undefined,
                    },
                ],
            ]}
        >
            {props.children}
        </Provider>
    );
}

/**
 * Low-level hook to create a card component.
 */
export function useCard(props: CardHookProps): CardResult {
    const { kind = 'default' } = props;

    return {
        className: clsx(
            'group/card relative border shadow',
            {
                default: 'rounded-lg border-card bg-base',
                glass: 'rounded-xl border-[var(--sp-color-border-trial-asset)] [background:var(--sp-color-bg-card-glass-default)] dark:border-base',
            }[kind],
            props.className
        ),
        ...useLabelProps(props),
    };
}
